import type { Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { typographyVariantStyles } from '../../typography-variants';
import type { EmptyStateVariant } from './types';

const MIN_HEIGHT: Record<EmptyStateVariant, number> = {
  primary: 260,
  tertiary: 140,
};

const CONTENT_WIDTH = 510;

const accentColor = (theme: Theme, variant: EmptyStateVariant) =>
  variant === 'primary' ? theme.colors.text.primary : theme.colors.text.tertiary;

const withoutVariant = { shouldForwardProp: (prop: string) => prop !== '$variant' };

export const StyledEmptyState = styled(
  'div',
  withoutVariant,
)<{ $variant: EmptyStateVariant }>(({ theme, $variant }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing[12],
  width: '100%',
  minHeight: MIN_HEIGHT[$variant],
  padding: theme.spacing[16],
  textAlign: 'center',
  wordBreak: 'break-word',
}));

export const StyledIcon = styled(
  'span',
  withoutVariant,
)<{ $variant: EmptyStateVariant }>(({ theme, $variant }) => ({
  display: 'inline-flex',
  flexShrink: 0,
  color: accentColor(theme, $variant),

  svg: {
    width: 24,
    height: 24,
  },

  // Glyphs passed through `icon` come from exports of different sizes (users.svg is 14px);
  // non-scaling-stroke keeps every one at 1.5 rendered px once drawn at 24.
  'svg, svg *': {
    strokeWidth: 1.5,
    vectorEffect: 'non-scaling-stroke',
  },
}));

export const StyledText = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[4],
  width: '100%',
  maxWidth: CONTENT_WIDTH - theme.spacing[16] * 2,
}));

export const StyledTitle = styled(
  'p',
  withoutVariant,
)<{ $variant: EmptyStateVariant }>(({ theme, $variant }) => ({
  margin: 0,
  ...typographyVariantStyles.body2,
  fontWeight: 500,
  color: accentColor(theme, $variant),
}));

export const StyledDescription = styled('p')(({ theme }) => ({
  margin: 0,
  ...typographyVariantStyles.body3,
  fontWeight: 400,
  color: theme.colors.text.secondary,
}));
